// Shared helpers for the Signals & Playbooks two-pane settings surface.
// See docs/plans/2026-07-05-signals-playbooks-fe-redesign.md (pano repo) for the
// design decisions these encode — especially the draft state machine (S2), the
// content-only dirty compare (S4), and the link round-trip (B3).

export const PRIORITY_META = {
  high: { label: "High", color: "#E11D48", dotClass: "bg-rose-600" },
  medium: { label: "Medium", color: "#D97706", dotClass: "bg-amber-600" },
  low: { label: "Low", color: "#16A34A", dotClass: "bg-green-600" },
};

export const PRIORITY_ORDER = ["high", "medium", "low"];
export const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };

// Grow a textarea to fit its content: reset to auto so scrollHeight reflects the
// real content height, then pin the element height to it. Pure DOM mutation,
// extracted so the mechanism is unit-testable (jsdom can't measure scrollHeight).
export const autosize = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

// Real backend category vocabulary (apps/pano/ai/constants.py) — uppercase.
export const CAT_ORDER = [
  "HEALTH",
  "RELATIONSHIP",
  "USAGE",
  "GROWTH",
  "CONTRACT",
];
export const OTHER_CATEGORY = "OTHER";

export const CAT_LABEL_KEYS = {
  HEALTH: "health",
  RELATIONSHIP: "relationship",
  USAGE: "usage",
  GROWTH: "growth",
  CONTRACT: "contract",
  [OTHER_CATEGORY]: "other",
};

export const categoryLabelKey = (category) =>
  CAT_LABEL_KEYS[CAT_ORDER.includes(category) ? category : OTHER_CATEGORY];

let keyCounter = 0;
// eslint-disable-next-line no-plusplus
export const nextDraftKey = () => `draft-${keyCounter++}`;

export const toLocalItem = (action, key) => ({
  _key: key || action.id || nextDraftKey(),
  id: action.id,
  text: action.text,
  priority: action.priority || "medium",
  link: action.link,
});

// new items (no id) omit id from the payload; link is preserved verbatim so a
// full-replacement PATCH never wipes a link on an untouched row (B3).
export const toPayloadItem = ({ id, text, priority, link }) => {
  const item = { text, priority };
  if (id) item.id = id;
  if (link) item.link = link;
  return item;
};

// Content-only compare (S4): [text, priority, link] — deliberately NOT
// id-sensitive, so server-assigned ids after a save never read as dirty.
export const actionsEqual = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  return a.every((item, i) => {
    const other = b[i] || {};
    return (
      item.text === other.text &&
      item.priority === other.priority &&
      (item.link || "") === (other.link || "")
    );
  });
};

export const isRanked = (actions) => {
  for (let i = 1; i < actions.length; i += 1) {
    if (
      PRIORITY_RANK[actions[i].priority] <
      PRIORITY_RANK[actions[i - 1].priority]
    ) {
      return false;
    }
  }
  return true;
};

export const groupPlaybooks = (playbooks) => {
  const buckets = {};
  playbooks.forEach((p) => {
    const key = CAT_ORDER.includes(p.category) ? p.category : OTHER_CATEGORY;
    if (!buckets[key]) buckets[key] = [];
    buckets[key].push(p);
  });
  return [...CAT_ORDER, OTHER_CATEGORY]
    .filter((key) => buckets[key] && buckets[key].length > 0)
    .map((key) => ({ key, items: buckets[key] }));
};

export const filterPlaybooks = (playbooks, filter) => {
  if (filter === "customized")
    return playbooks.filter((p) => p.source === "team");
  if (filter === "off") return playbooks.filter((p) => !p.is_enabled);
  return playbooks;
};
